import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useBionicReading } from '../contexts/BionicReadingContext';
import { BionicText } from '../components/custom/BionicText';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { 
  ArrowLeft, 
  Sun, 
  Moon, 
  Contrast, 
  Eye,
  LogOut,
  Loader2,
  Save
} from 'lucide-react';
import { toast } from 'sonner';

export default function Settings() {
  const { user, token, isAuthenticated, loading: authLoading, updateProfile, logout } = useAuth();
  const { theme, toggleTheme, highContrast, toggleHighContrast, isDark } = useTheme();
  const { bionicEnabled, toggleBionic } = useBionicReading();
  const navigate = useNavigate();
  
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      await updateProfile({
        theme,
        bionic_reading: bionicEnabled,
        high_contrast: highContrast
      });
      toast.success('Preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Signed out successfully');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <main className="min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8 opacity-0 animate-fade-in">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="mb-2 -ml-2"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
            Back to Dashboard
          </Button>
          
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
            <BionicText>Settings</BionicText>
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your account and accessibility preferences
          </p>
        </div>

        <div className="space-y-6">
          {/* Account */}
          <Card className="opacity-0 animate-fade-in stagger-1" data-testid="account-card">
            <CardHeader>
              <CardTitle>Account</CardTitle>
              <CardDescription>Your account information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-muted-foreground">Email</Label>
                <p className="font-medium text-foreground">{user?.email}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Member since</Label>
                <p className="font-medium text-foreground">
                  {user?.created_at 
                    ? new Date(user.created_at).toLocaleDateString() 
                    : 'N/A'
                  }
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Accessibility */}
          <Card className="opacity-0 animate-fade-in stagger-2" data-testid="accessibility-card">
            <CardHeader>
              <CardTitle>Accessibility</CardTitle>
              <CardDescription>
                Customize your viewing experience for better readability
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Theme Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isDark ? (
                    <Moon className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                  ) : (
                    <Sun className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                  )}
                  <div>
                    <Label className="font-medium">Dark Mode</Label>
                    <p className="text-sm text-muted-foreground">
                      Switch between light and dark themes
                    </p>
                  </div>
                </div>
                <Switch
                  checked={theme === 'dark'}
                  onCheckedChange={toggleTheme}
                  aria-label="Toggle dark mode"
                  data-testid="dark-mode-switch"
                />
              </div>

              <Separator />

              {/* High Contrast */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Contrast className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                  <div>
                    <Label className="font-medium">High Contrast</Label>
                    <p className="text-sm text-muted-foreground">
                      Increase color contrast for better visibility
                    </p>
                  </div>
                </div>
                <Switch
                  checked={highContrast}
                  onCheckedChange={toggleHighContrast}
                  aria-label="Toggle high contrast mode"
                  data-testid="high-contrast-switch"
                />
              </div>

              <Separator />

              {/* Bionic Reading */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Eye className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                  <div>
                    <Label className="font-medium">Bionic Reading</Label>
                    <p className="text-sm text-muted-foreground">
                      Bold the first half of words for faster reading
                    </p>
                  </div>
                </div>
                <Switch
                  checked={bionicEnabled}
                  onCheckedChange={toggleBionic}
                  aria-label="Toggle bionic reading"
                  data-testid="bionic-reading-switch"
                />
              </div>

              {/* Preview */}
              <div className="bg-muted/50 rounded-xl p-4">
                <Label className="text-xs text-muted-foreground mb-2 block">Preview</Label>
                <p className="text-foreground">
                  <BionicText>
                    This is how text will appear with your current settings. Bionic reading helps you read faster by guiding your eyes.
                  </BionicText>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Diet Preferences */}
          <Card className="opacity-0 animate-fade-in stagger-3" data-testid="diet-preferences-card">
            <CardHeader>
              <CardTitle>Diet Preferences</CardTitle>
              <CardDescription>
                Your saved dietary preferences (editable when creating plans)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {user?.diet_preferences && user.diet_preferences.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {user.diet_preferences.map((diet) => (
                    <Badge key={diet} variant="secondary">{diet}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">
                  No diet preferences saved yet
                </p>
              )}

              {user?.allergens && user.allergens.length > 0 && (
                <div className="mt-4">
                  <Label className="text-muted-foreground text-sm">Allergens</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {user.allergens.map((allergen) => (
                      <Badge key={allergen} variant="outline">{allergen}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4">
            <Button
              onClick={handleSavePreferences}
              disabled={saving}
              className="rounded-full gap-2"
              data-testid="save-preferences-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Save className="h-4 w-4" aria-hidden="true" />
              )}
              Save Preferences
            </Button>
            
            <Button
              variant="outline"
              onClick={handleLogout}
              className="rounded-full gap-2 text-destructive hover:text-destructive"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    </main>
  );
}
